import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, FileSpreadsheet, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import SearchableAgentSelect from "@/components/common/SearchableAgentSelect";
import { cn } from "@/lib/utils";
import { buildMrImportPreview } from "@/utils/tlpMrExcelImport";

const MrExcelImportModal = ({
  open,
  onClose,
  fileName,
  parsed,
  agents = [],
  defaultYear,
  isDark,
  textClass,
  labelClass,
  importing,
  parsing = false,
  companyMissing = false,
  onConfirm,
  importResult,
}) => {
  const [selectedBlockIds, setSelectedBlockIds] = useState({});
  const [agentOverrides, setAgentOverrides] = useState({});
  const [expandedAssistant, setExpandedAssistant] = useState(null);
  const [step, setStep] = useState("preview");

  useEffect(() => {
    if (!open) return;
    setStep(importResult ? "done" : "preview");
    setAgentOverrides({});
    setExpandedAssistant(null);
    if (!parsed?.blocks?.length) return;
    const sel = {};
    parsed.blocks.forEach((b) => { sel[b.id] = true; });
    setSelectedBlockIds(sel);
  }, [open, parsed, importResult]);

  const selectedIds = useMemo(
    () => Object.entries(selectedBlockIds).filter(([, v]) => v).map(([k]) => k),
    [selectedBlockIds],
  );

  const preview = useMemo(() => {
    if (!parsed?.blocks?.length) return null;
    const overrideIds = {};
    Object.entries(agentOverrides).forEach(([name, agent]) => {
      if (agent?.id) overrideIds[name] = agent.id;
    });
    return buildMrImportPreview(parsed, agents, selectedIds, overrideIds);
  }, [parsed, agents, selectedIds, agentOverrides]);

  const handleConfirm = useCallback(() => {
    const rows = (preview?.rows ?? []).map((r) => ({
      assistant_name: r.assistant_name,
      agent_id: Number(r.agent_id),
      report_date: String(r.report_date).slice(0, 10),
      net_count: r.net_count ?? 0,
    })).filter((r) => r.assistant_name && r.agent_id > 0 && /^\d{4}-\d{2}-\d{2}$/.test(r.report_date));
    if (!rows.length) return;
    onConfirm({
      source_file: fileName,
      period_start: parsed?.blocks?.find((b) => selectedIds.includes(b.id))?.period_start,
      period_end: parsed?.blocks?.filter((b) => selectedIds.includes(b.id)).slice(-1)[0]?.period_end,
      rows,
    });
  }, [preview, onConfirm, fileName, parsed, selectedIds]);

  const panelClass = isDark ? "bg-slate-900 border-slate-700 text-white" : "";
  const blockClass = isDark ? "border-slate-700 bg-slate-950/50" : "border-gray-200 bg-gray-50/80";
  const filterTriggerClass = cn(
    "h-9 min-h-9 w-full rounded-md border text-sm font-normal shadow-sm",
    isDark ? "border-slate-700 bg-slate-900 text-slate-100" : "border-input bg-background",
  );

  const canImport = !companyMissing && (preview?.importable_count ?? 0) > 0;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !importing && !parsing && onClose()}>
      <DialogContent
        className={`max-w-3xl max-h-[92vh] overflow-hidden flex flex-col ${panelClass}`}
        onPointerDownOutside={(e) => {
          const t = e.target;
          if (t instanceof Element && t.closest("[data-radix-popover-content]")) e.preventDefault();
        }}
        onFocusOutside={(e) => {
          const t = e.target;
          if (t instanceof Element && t.closest("[data-radix-popover-content]")) e.preventDefault();
        }}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2 pr-6">
            <FileSpreadsheet className={`w-5 h-5 shrink-0 ${isDark ? "text-violet-400" : "text-violet-600"}`} />
            MR Excel import
          </DialogTitle>
          {fileName && <p className={`text-xs font-normal ${labelClass}`}>{fileName}</p>}
          {defaultYear && (
            <p className={`text-[10px] font-normal ${labelClass}`}>
              Kısa tarihler {defaultYear} yılına yazılır (ör. «1 Ekim»).
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-1 pr-1">
          {parsing && (
            <div className={`rounded-lg border p-6 flex flex-col items-center gap-3 ${blockClass}`}>
              <Loader2 className={`w-8 h-8 animate-spin ${isDark ? "text-violet-400" : "text-violet-600"}`} />
              <p className={`text-sm ${textClass}`}>Excel okunuyor…</p>
            </div>
          )}

          {!parsing && companyMissing && step === "preview" && (
            <div className={`rounded-lg border p-3 flex gap-2 ${isDark ? "border-amber-700/50 bg-amber-950/20" : "border-amber-200 bg-amber-50/90"}`}>
              <AlertTriangle className={`w-5 h-5 shrink-0 ${isDark ? "text-amber-400" : "text-amber-700"}`} />
              <p className={`text-xs ${isDark ? "text-amber-100" : "text-amber-900"}`}>
                Önizleme açık; kaydetmek için sayfada firma seçmeniz gerekir.
              </p>
            </div>
          )}

          {step === "done" && importResult && (
            <div className={`rounded-lg border p-4 ${blockClass}`}>
              <div className="flex items-start gap-3">
                <CheckCircle2 className={`w-8 h-8 shrink-0 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} />
                <div>
                  <p className={`text-sm font-semibold ${textClass}`}>Kayıt tamamlandı</p>
                  <ul className={`text-xs mt-2 space-y-1 ${labelClass}`}>
                    <li>{importResult.inserted ?? 0} yeni kayıt</li>
                    {(importResult.updated ?? 0) > 0 && (
                      <li>{importResult.updated} kayıt güncellendi (aynı agent + gün)</li>
                    )}
                    {(importResult.unchanged ?? 0) > 0 && (
                      <li>{importResult.unchanged} kayıt zaten aynıydı (atlandı)</li>
                    )}
                    {(importResult.skipped ?? 0) > 0 && (
                      <li>{importResult.skipped} satır atlandı (agent eşleşmedi veya eksik)</li>
                    )}
                    {(importResult.skip_date ?? 0) > 0 && (
                      <li>{importResult.skip_date} satır geçersiz tarih</li>
                    )}
                    {(importResult.skip_agent ?? 0) > 0 && (
                      <li>{importResult.skip_agent} satır agent eşleşmedi</li>
                    )}
                    {(importResult.unmatched?.length ?? 0) > 0 && (
                      <li className={isDark ? "text-amber-300" : "text-amber-800"}>
                        Eşleşmeyen: {importResult.unmatched.slice(0, 12).join(", ")}
                        {importResult.unmatched.length > 12 ? ` (+${importResult.unmatched.length - 12})` : ""}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {!parsing && step === "preview" && parsed?.error && (
            <p className={`text-sm ${isDark ? "text-red-300" : "text-red-700"}`}>{parsed.error}</p>
          )}

          {!parsing && step === "preview" && parsed?.blocks?.length > 0 && (
            <>
              <div className={`rounded-lg border p-3 space-y-2 ${blockClass}`}>
                <p className={`text-xs font-medium ${textClass}`}>Tablo blokları (dönem seçin)</p>
                <ul className="space-y-2 max-h-36 overflow-y-auto">
                  {parsed.blocks.map((b) => (
                    <li key={b.id} className="flex items-start gap-2 text-xs">
                      <Checkbox
                        checked={!!selectedBlockIds[b.id]}
                        onCheckedChange={(v) => setSelectedBlockIds((prev) => ({ ...prev, [b.id]: !!v }))}
                      />
                      <div className="min-w-0">
                        <span className={`font-medium ${textClass}`}>{b.title}</span>
                        <span className={`block ${labelClass}`}>
                          {b.sheet_name} · {b.period_start} – {b.period_end} · {b.assistants.length} asistan · {b.record_count} gün×kişi
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {preview && (
                <div className={`rounded-lg border p-3 ${blockClass}`}>
                  <p className={`text-xs mb-2 ${textClass}`}>
                    <strong>{preview.matched_count}</strong> asistan eşleşti
                    {preview.unmatched_count > 0 && (
                      <> · <strong className={isDark ? "text-amber-300" : "text-amber-800"}>{preview.unmatched_count}</strong> eşleşmedi</>
                    )}
                    {" · "}
                    <strong>{preview.importable_count}</strong> günlük kayıt içeri aktarılacak
                    {(preview.duplicate_in_file_merged ?? 0) > 0 && (
                      <> · dosyada {preview.duplicate_in_file_merged} tekrar birleştirildi</>
                    )}
                  </p>
                  <p className={`text-[11px] mt-1 ${labelClass}`}>
                    Aynı agent ve aynı gün için panelde kayıt varsa sayı güncellenir; ikinci satır eklenmez.
                  </p>

                  {preview.unmatched_count > 0 && (
                    <div className={`rounded-md border p-2 mb-3 flex gap-2 ${isDark ? "border-amber-700/40 bg-amber-950/20" : "border-amber-200 bg-amber-50/80"}`}>
                      <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${isDark ? "text-amber-400" : "text-amber-700"}`} />
                      <p className={`text-[11px] ${isDark ? "text-amber-100" : "text-amber-900"}`}>
                        Eşleşmeyen asistanlar için panel agent seçin; seçilmezse o kişinin günleri kaydedilmez.
                      </p>
                    </div>
                  )}

                  <ul className="space-y-1 max-h-[min(50vh,420px)] overflow-y-auto">
                    {preview.assistants.map((a) => {
                      const isOpen = expandedAssistant === a.assistant_name;
                      const unmatched = a.match_status !== "matched";
                      return (
                        <li
                          key={a.assistant_name}
                          className={`rounded-md border text-xs ${isDark ? "border-slate-700" : "border-gray-200"}`}
                        >
                          <button
                            type="button"
                            className={`w-full flex items-center gap-2 px-2 py-2 text-left ${isDark ? "hover:bg-slate-800/60" : "hover:bg-gray-50"}`}
                            onClick={() => setExpandedAssistant(isOpen ? null : a.assistant_name)}
                          >
                            {isOpen ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
                            <span className={`font-medium flex-1 min-w-0 truncate ${textClass}`}>{a.assistant_name}</span>
                            <span className={`tabular-nums ${labelClass}`}>top. {a.total_net}</span>
                            {unmatched ? (
                              <span className={isDark ? "text-amber-400" : "text-amber-700"}>eşleşmedi</span>
                            ) : (
                              <span className={isDark ? "text-emerald-400" : "text-emerald-700"}>{a.agent_name}</span>
                            )}
                          </button>
                          {unmatched && (
                            <div className="px-2 pb-2">
                              <SearchableAgentSelect
                                agents={agents}
                                value={agentOverrides[a.assistant_name] ?? null}
                                onChange={(agent) => setAgentOverrides((prev) => ({ ...prev, [a.assistant_name]: agent }))}
                                placeholder="Panel agent seç…"
                                isDark={isDark}
                                inputClass={filterTriggerClass}
                                labelClass={labelClass}
                                size="default"
                                inDialog
                              />
                            </div>
                          )}
                          {isOpen && (
                            <div className={`px-2 pb-2 flex flex-wrap gap-1 border-t pt-2 ${isDark ? "border-slate-800" : "border-gray-100"}`}>
                              {(a.daily || []).map((d) => (
                                <span key={d.report_date} className={`rounded px-1.5 py-0.5 tabular-nums ${isDark ? "bg-slate-800 text-slate-200" : "bg-white border border-gray-200"}`}>
                                  {d.report_date.slice(8, 10)}.{d.report_date.slice(5, 7)}: {d.net_count}
                                </span>
                              ))}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2 shrink-0">
          {step === "done" ? (
            <Button type="button" onClick={onClose}>Tamam</Button>
          ) : (
            <>
              <Button type="button" variant="outline" disabled={importing || parsing} onClick={onClose}>
                İptal
              </Button>
              <Button type="button" disabled={importing || parsing || !canImport} onClick={handleConfirm}>
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {companyMissing
                  ? "Firma seçin"
                  : canImport
                    ? `${preview.importable_count} kaydı içeri aktar`
                    : "Eşleşen kayıt yok"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MrExcelImportModal;
